import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Spin } from "antd";

import { issuer } from "../auth-client.ts";
import { createAuthChallengeClient, type AuthChallenge } from "./auth-challenge-client.ts";

const CAPTCHA_WIDTH = 300;
const CAPTCHA_HEIGHT = 160;
const PIECE_SIZE = 44;

function displayPieceSize(trackWidth: number) {
  return PIECE_SIZE * (Math.max(trackWidth, 1) / CAPTCHA_WIDTH);
}

export function normalizeCaptchaVerifyX(sliderX: number, trackWidth: number) {
  const safeTrackWidth = Math.max(trackWidth || CAPTCHA_WIDTH, 1);
  const sliderMax = Math.max(safeTrackWidth - displayPieceSize(safeTrackWidth), 1);
  return Math.round(
    (Math.max(0, Math.min(sliderX, sliderMax)) / sliderMax) * (CAPTCHA_WIDTH - PIECE_SIZE),
  );
}

type ChallengeStatus = "idle" | "verifying" | "failed" | "verified";

export function AuthSliderChallenge({
  token,
  onVerified,
}: {
  token: string;
  onVerified(token: string): void;
}) {
  const client = useRef(createAuthChallengeClient(issuer)).current;
  const [challenge, setChallenge] = useState<AuthChallenge>();
  const [status, setStatus] = useState<ChallengeStatus>("idle");
  const [sliderX, setSliderX] = useState(0);
  const [trackWidth, setTrackWidth] = useState(CAPTCHA_WIDTH);
  const sliderXRef = useRef(0);
  const trackRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ clientX: number; sliderX: number } | undefined>(undefined);
  const deliveredTokenRef = useRef("");
  const refreshTimerRef = useRef<number | undefined>(undefined);
  const loadInFlightRef = useRef(false);
  const onVerifiedRef = useRef(onVerified);

  useEffect(() => {
    onVerifiedRef.current = onVerified;
  }, [onVerified]);

  const load = useCallback(async () => {
    if (loadInFlightRef.current) return;
    loadInFlightRef.current = true;
    if (refreshTimerRef.current !== undefined) window.clearTimeout(refreshTimerRef.current);
    onVerifiedRef.current("");
    deliveredTokenRef.current = "";
    setChallenge(undefined);
    setStatus("idle");
    setSliderX(0);
    sliderXRef.current = 0;
    try {
      setChallenge(await client.generate());
    } catch {
      setStatus("failed");
    } finally {
      loadInFlightRef.current = false;
    }
  }, [client]);

  useEffect(() => {
    void load();
    return () => {
      if (refreshTimerRef.current !== undefined) window.clearTimeout(refreshTimerRef.current);
    };
  }, [load]);

  useEffect(() => {
    if (!challenge || !trackRef.current) return;
    const track = trackRef.current;
    const updateWidth = () => {
      const nextWidth = Math.min(CAPTCHA_WIDTH, Math.max(track.clientWidth, 1));
      setTrackWidth(nextWidth);
      const maximum = nextWidth - displayPieceSize(nextWidth);
      sliderXRef.current = Math.min(sliderXRef.current, maximum);
      setSliderX(sliderXRef.current);
    };
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(track);
    return () => observer.disconnect();
  }, [challenge]);

  useEffect(() => {
    if (status === "verified" && deliveredTokenRef.current && token !== deliveredTokenRef.current) {
      void load();
    }
  }, [load, status, token]);

  const moveTo = (next: number) => {
    const maximum = Math.max(trackWidth - displayPieceSize(trackWidth), 0);
    const bounded = Math.max(0, Math.min(next, maximum));
    sliderXRef.current = bounded;
    setSliderX(bounded);
  };

  const verifyCurrentPosition = useCallback(async () => {
    if (!challenge || status === "verified" || status === "verifying") return;
    setStatus("verifying");
    const verified = await client.verify(
      challenge.captchaId,
      normalizeCaptchaVerifyX(sliderXRef.current, trackRef.current?.clientWidth || trackWidth),
    ).catch(() => false);
    if (verified) {
      deliveredTokenRef.current = challenge.captchaId;
      setStatus("verified");
      onVerifiedRef.current(challenge.captchaId);
      return;
    }
    setStatus("failed");
    refreshTimerRef.current = window.setTimeout(() => { void load(); }, 500);
  }, [challenge, client, load, status, trackWidth]);

  if (!challenge) {
    return (
      <div className="auth-slider-loading">
        {status === "failed" ? (
          <Button onClick={() => void load()}>重新加载验证</Button>
        ) : <Spin />}
      </div>
    );
  }

  const scale = trackWidth / CAPTCHA_WIDTH;
  const pieceSize = displayPieceSize(trackWidth);
  const maximum = Math.max(trackWidth - pieceSize, 0);

  return (
    <div className={`auth-slider-challenge auth-slider-${status}`}>
      <div className="auth-slider-picture" style={{ height: CAPTCHA_HEIGHT * scale }}>
        <img src={challenge.bgImage} alt="" draggable={false} />
        <img
          className="auth-slider-piece"
          src={challenge.pieceImage}
          alt=""
          draggable={false}
          style={{
            left: sliderX,
            top: challenge.targetY * scale,
            width: pieceSize,
            height: pieceSize,
          }}
        />
        {status === "verified" ? <div className="auth-slider-success">验证通过</div> : null}
      </div>
      <div ref={trackRef} className="auth-slider-track">
        <div className="auth-slider-progress" style={{ width: sliderX + pieceSize / 2 }} />
        <span>{status === "verified" ? "" : status === "verifying" ? "正在验证…" : "拖动滑块完成拼图"}</span>
        <div
          className="auth-slider-handle"
          style={{ left: sliderX, width: pieceSize }}
          onPointerDown={(event) => {
            if (status !== "idle") return;
            event.preventDefault();
            event.currentTarget.setPointerCapture(event.pointerId);
            dragRef.current = { clientX: event.clientX, sliderX: sliderXRef.current };
          }}
          onPointerMove={(event) => {
            if (!dragRef.current) return;
            moveTo(dragRef.current.sliderX + event.clientX - dragRef.current.clientX);
          }}
          onPointerUp={(event) => {
            if (!dragRef.current) return;
            dragRef.current = undefined;
            if (event.currentTarget.hasPointerCapture(event.pointerId)) {
              event.currentTarget.releasePointerCapture(event.pointerId);
            }
            void verifyCurrentPosition();
          }}
          onKeyDown={(event) => {
            if (status !== "idle") return;
            if (event.key === "ArrowLeft" || event.key === "ArrowDown") {
              event.preventDefault();
              moveTo(sliderXRef.current - 5);
            } else if (event.key === "ArrowRight" || event.key === "ArrowUp") {
              event.preventDefault();
              moveTo(sliderXRef.current + 5);
            } else if (event.key === "Home") {
              event.preventDefault();
              moveTo(0);
            } else if (event.key === "End") {
              event.preventDefault();
              moveTo(maximum);
            } else if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              void verifyCurrentPosition();
            }
          }}
          role="slider"
          aria-label="拼图滑块验证"
          aria-valuemin={0}
          aria-valuemax={Math.round(maximum)}
          aria-valuenow={Math.round(sliderX)}
          aria-disabled={status !== "idle"}
          tabIndex={status === "idle" ? 0 : -1}
        >
          {status === "verified" ? "✓" : "⇥"}
        </div>
      </div>
      <div aria-live="polite">
        {status === "failed" ? <span className="auth-slider-error">验证失败，正在刷新…</span> : null}
      </div>
      {status !== "verified" && status !== "verifying" ? (
        <Button type="link" onClick={() => void load()}>刷新验证码</Button>
      ) : null}
    </div>
  );
}
